import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { getPolicy, getRevision, listPolicies, listRevisions } from './policiesApi';

const SUMMARY_MAX_LENGTH = 80;

function policyKey(policy) {
  return `${policy.owner_username}/${policy.name}`;
}

// Collapsed to one line and capped, same convention as
// DraftPolicies.js's draftSummary - `description` can run long enough
// that a list item shouldn't just dump it verbatim.
function summarize(text, max = SUMMARY_MAX_LENGTH) {
  const singleLine = text.replace(/\s+/g, ' ').trim();
  if (singleLine.length <= max) return singleLine;
  return `${singleLine.slice(0, max - 1).trimEnd()}\u2026`;
}

function policyLabel(policy) {
  const key = policyKey(policy).toUpperCase();
  const visibility = policy.visibility === 'private' ? 'PRIVATE' : 'PUBLIC';
  const description = policy.description
    ? summarize(policy.description)
    : 'NO DESCRIPTION ON FILE';
  return `${key} (${visibility}) \u2014 ${description}`;
}

function formatTimestamp(iso) {
  if (!iso) return 'UNKNOWN';
  try {
    return new Date(iso).toLocaleString();
  } catch (err) {
    return iso;
  }
}

// Compact, single-line rendering of one rule (RuleOut in
// schemas_policy.py) - there's no rules-table styling in App.css yet
// and a policy can carry up to 10,000 of these, so a dense one-liner
// per rule (rendered in a `.view-list`, same as everywhere else this
// app lists things) is far more scannable than a full table would be
// at that scale.
function ruleTarget(rule) {
  if (rule.name_op === 'any') return 'ANY PACKAGE';
  if (rule.name_op === 'eq') return rule.name;
  return `${rule.name_op.toUpperCase()} "${rule.name}"`;
}

function ruleLabel(rule) {
  const parts = [`${rule.effect.toUpperCase()} ${rule.namespace.toUpperCase()} ${ruleTarget(rule)}`];
  if (rule.version_spec) parts.push(`VERSION ${rule.version_spec}`);
  if (rule.subdirs?.length) parts.push(`SUBDIRS ${rule.subdirs.join(', ')}`);
  if (rule.build_glob) parts.push(`BUILD ${rule.build_glob}`);
  if (rule.max_size != null) parts.push(`MAX SIZE ${rule.max_size}`);
  if (rule.requires_python) parts.push(`PYTHON ${rule.requires_python}`);
  if (rule.note) parts.push(`(${rule.note})`);
  return `#${rule.ordinal} \u2014 ${parts.join(', ')}`;
}

export default function Policies({ onError }) {
  const auth = useAuth();
  const accessToken = auth.user?.access_token;

  // `view` mirrors DraftPolicies.js's pattern: 'list' shows every
  // visible policy (own + public - GET /api/policies); 'detail' drills
  // into one, keyed by `{owner}/{name}` since that's how the backend
  // addresses a policy (there's no separate policy id route). Channel
  // bindings are deliberately never fetched or shown here.
  const [view, setView] = useState('list');

  const [status, setStatus] = useState('loading'); // loading | error | ready
  const [policies, setPolicies] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [nextCursor, setNextCursor] = useState(null);
  const [loadMoreStatus, setLoadMoreStatus] = useState('idle'); // idle | loading

  const [detailKey, setDetailKey] = useState(null); // { owner, name } | null
  const [detailStatus, setDetailStatus] = useState('idle'); // idle | loading | error | ready
  const [detailError, setDetailError] = useState('');
  const [detailPolicy, setDetailPolicy] = useState(null);
  const [detailRevisions, setDetailRevisions] = useState([]);
  const [detailLatestRevision, setDetailLatestRevision] = useState(null);

  const fetchPolicies = useCallback(async () => {
    setStatus('loading');
    try {
      const result = await listPolicies(accessToken);
      setPolicies(result.items);
      setNextCursor(result.nextCursor);
      setStatus('ready');
    } catch (err) {
      setErrorMessage(err.message || 'Could not load policies. Please try again.');
      setStatus('error');
    }
  }, [accessToken]);

  useEffect(() => {
    fetchPolicies();
  }, [fetchPolicies]);

  async function handleLoadMore() {
    if (!nextCursor || loadMoreStatus === 'loading') return;
    setLoadMoreStatus('loading');
    try {
      const result = await listPolicies(accessToken, { cursor: nextCursor });
      setPolicies((prev) => [...prev, ...result.items]);
      setNextCursor(result.nextCursor);
      setLoadMoreStatus('idle');
    } catch (err) {
      setErrorMessage(err.message || 'Could not load more policies. Please try again.');
      setLoadMoreStatus('idle');
      onError?.();
    }
  }

  const fetchDetail = useCallback(async (owner, name) => {
    setDetailStatus('loading');
    setDetailError('');
    try {
      const [policyResult, revisionsResult] = await Promise.all([
        getPolicy(accessToken, owner, name),
        listRevisions(accessToken, owner, name),
      ]);
      setDetailPolicy(policyResult);
      setDetailRevisions(revisionsResult);
      // The first entry is always the one currently in effect (newest
      // first - see listRevisions' docstring in policiesApi.js). Fetch
      // its full rule list separately since list_revisions never
      // includes rules.
      const latest = revisionsResult[0];
      const latestDetail = latest
        ? await getRevision(accessToken, owner, name, latest.revision)
        : null;
      setDetailLatestRevision(latestDetail);
      setDetailStatus('ready');
    } catch (err) {
      setDetailError(err.message || 'Could not load this policy. Please try again.');
      setDetailStatus('error');
    }
  }, [accessToken]);

  function openDetail(owner, name) {
    setView('detail');
    setDetailKey({ owner, name });
    setDetailPolicy(null);
    setDetailRevisions([]);
    setDetailLatestRevision(null);
    setDetailError('');
    fetchDetail(owner, name);
  }

  function backToList() {
    setView('list');
    setDetailKey(null);
    setDetailStatus('idle');
    setDetailError('');
    setDetailPolicy(null);
    setDetailRevisions([]);
    setDetailLatestRevision(null);
  }

  return (
    <div className="terminal-box view-panel">
      <p className="form-title">== POLICIES ==</p>

      {view === 'list' && status === 'loading' && (
        <p className="dim-line">CONSULTING THE LEDGER OF CODIFIED LAWS...</p>
      )}

      {view === 'list' && status === 'error' && (
        <>
          <p className="error-line">! {errorMessage}</p>
          <div className="button-row">
            <button
              type="button"
              className="retro-button retro-button--ghost"
              onClick={fetchPolicies}
            >
              RETRY
            </button>
          </div>
        </>
      )}

      {view === 'list' && status === 'ready' && policies.length === 0 && (
        <p className="dim-line">
          NO POLICIES ON FILE. THE VILLAGE ELDERS HAVE NOT YET CODIFIED THE
          ANCIENT LAW OF &quot;DO NOT LEAVE THY THATCHED ROOF UNATTENDED.&quot;
        </p>
      )}

      {view === 'list' && status === 'ready' && policies.length > 0 && (
        <>
          <ul className="view-list">
            {policies.map((policy) => (
              <li key={policy.id}>
                <button
                  type="button"
                  className="view-list-item"
                  onClick={() => openDetail(policy.owner_username, policy.name)}
                >
                  {policyLabel(policy)}
                </button>
              </li>
            ))}
          </ul>

          {nextCursor && (
            <div className="button-row">
              <button
                type="button"
                className="retro-button retro-button--ghost"
                onClick={handleLoadMore}
                disabled={loadMoreStatus === 'loading'}
              >
                {loadMoreStatus === 'loading' ? 'LOADING...' : 'LOAD MORE'}
              </button>
            </div>
          )}
        </>
      )}

      {view === 'detail' && detailStatus === 'loading' && (
        <p className="dim-line">UNSEALING THE ARCHIVED DECREE...</p>
      )}

      {view === 'detail' && detailStatus === 'error' && (
        <>
          <p className="error-line">! {detailError}</p>
          <div className="button-row">
            <button
              type="button"
              className="retro-button retro-button--ghost"
              onClick={() => fetchDetail(detailKey.owner, detailKey.name)}
            >
              RETRY
            </button>
            <button
              type="button"
              className="retro-button retro-button--ghost"
              onClick={backToList}
            >
              &laquo; BACK
            </button>
          </div>
        </>
      )}

      {view === 'detail' && detailStatus === 'ready' && detailPolicy && (
        <>
          <p className="dim-line">
            {policyKey(detailPolicy).toUpperCase()}
            {' \u2014 '}
            {detailPolicy.visibility === 'private' ? 'PRIVATE' : 'PUBLIC'}
          </p>
          <p className="dim-line">
            {detailPolicy.description
              ? detailPolicy.description
              : 'NO DESCRIPTION ON FILE.'}
          </p>
          <p className="dim-line">CODIFIED {formatTimestamp(detailPolicy.created_at)}</p>

          {detailLatestRevision ? (
            <>
              <p className="form-title form-title--section">
                == CURRENT REVISION {detailLatestRevision.revision} ==
              </p>
              <p className="dim-line">
                AUTHORED BY {detailLatestRevision.authored_by.toUpperCase()}, DEFAULT
                EFFECT {detailLatestRevision.default_effect.toUpperCase()},{' '}
                {formatTimestamp(detailLatestRevision.created_at)}
              </p>

              {detailLatestRevision.freeform_text && (
                <p className="dim-line">
                  ORIGINAL WISHES: &quot;{detailLatestRevision.freeform_text}&quot;
                </p>
              )}
              {detailLatestRevision.notes && (
                <p className="dim-line">NOTES: {detailLatestRevision.notes}</p>
              )}

              {detailLatestRevision.rules.length === 0 ? (
                <p className="dim-line">
                  NO RULES ON THE BOOKS - EVERY PACKAGE FALLS TO THE DEFAULT
                  EFFECT ABOVE.
                </p>
              ) : (
                <ul className="view-list">
                  {detailLatestRevision.rules.map((rule) => (
                    <li key={rule.id}>{ruleLabel(rule)}</li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <p className="dim-line">THIS LAW HAS NO REVISIONS YET.</p>
          )}

          {detailRevisions.length > 1 && (
            <>
              <p className="form-title form-title--section">
                == REVISION HISTORY ==
              </p>
              <ul className="view-list">
                {detailRevisions.map((revision) => (
                  <li key={revision.id}>
                    REVISION {revision.revision}
                    {revision.revision === detailLatestRevision?.revision ? ' (CURRENT)' : ''}
                    {' \u2014 '}
                    {revision.authored_by.toUpperCase()}, {formatTimestamp(revision.created_at)}
                  </li>
                ))}
              </ul>
            </>
          )}

          <div className="button-row">
            <button
              type="button"
              className="retro-button retro-button--ghost"
              onClick={backToList}
            >
              &laquo; BACK
            </button>
          </div>
        </>
      )}
    </div>
  );
}
